import { Seo } from "@/components/Seo";
import { PageIntro } from "@/components/PageIntro";
import { Reveal } from "@/components/Motion";
import { Img } from "@/components/Img";
import { SectionLabel } from "@/components/SectionLabel";
import { ProcessSteps } from "@/components/ProcessSteps";
import { CtaBanner } from "@/components/CtaBanner";
import { COMPANY } from "@/data/site";

const PRINCIPLES = [
  { t: "Creativity with purpose", d: "Ideas are judged by how well they serve the brand and its audience, not by novelty alone." },
  { t: "Attention to detail", d: "Spacing, type, colour and finish are refined until every element feels intentional." },
  { t: "Clear communication", d: "Straightforward conversations, clear feedback rounds and no unnecessary jargon." },
  { t: "Consistent visual identity", d: "Every asset is designed to sit comfortably alongside the rest of your brand." },
  { t: "Solutions tailored to each project", d: "No templates. Each project starts with your goals, your audience and your requirements." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About PRPO LTD — UK Creative Design Company"
        description="PRPO LTD is a UK creative design company providing thoughtful graphic design, branding and digital design solutions for individuals and businesses."
        path="/about"
      />
      <PageIntro
        testid="about-intro"
        label="About us"
        lines={[{ content: "Design is how" }, { content: "a brand speaks", className: "text-royal" }, { content: "before it says a word." }]}
      />
      <section className="pb-24 sm:pb-36" aria-label="Our belief">
        <div className="shell grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-lg">
              <Img name="identity" alt="Concept brand stationery arranged on a warm white surface" sizes="(min-width:1024px) 40vw, 100vw" className="aspect-[4/5] w-full object-cover" />
              <span className="absolute bottom-4 left-4 rounded-full bg-lime px-3 py-1.5 font-mono text-[0.65rem] uppercase tracking-[0.16em] text-navy">Concept work</span>
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-10">
            <Reveal>
              <p className="font-display text-2xl font-semibold leading-[1.35] tracking-[-0.02em] text-navy sm:text-3xl" data-testid="about-statement">
                At PRPO LTD, we believe great design is more than appearance — it is a powerful way to communicate ideas, express a brand’s personality, and create meaningful connections.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-base leading-relaxed text-slatey sm:text-lg">
                We provide thoughtful, creative design solutions that help individuals and businesses present themselves with clarity, confidence, and consistency.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <dl className="mt-12 grid gap-6 border-t border-[var(--line)] pt-8 sm:grid-cols-2">
                <div><dt className="eyebrow text-royal">Company</dt><dd className="mt-2 text-navy">{COMPANY.name}</dd></div>
                <div><dt className="eyebrow text-royal">Activity</dt><dd className="mt-2 text-navy">{COMPANY.sic}</dd></div>
                <div><dt className="eyebrow text-royal">Based in</dt><dd className="mt-2 text-navy">Swadlincote, United Kingdom</dd></div>
                <div><dt className="eyebrow text-royal">Disciplines</dt><dd className="mt-2 text-navy">Graphic, brand, packaging & digital design</dd></div>
              </dl>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="bg-mist/70 py-24 sm:py-36" aria-labelledby="principles-heading" data-testid="about-principles">
        <div className="shell">
          <SectionLabel>Our principles</SectionLabel>
          <h2 id="principles-heading" className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-[-0.04em] text-navy sm:text-5xl lg:text-6xl">What guides every project.</h2>
          <ol className="mt-14">
            {PRINCIPLES.map((p, i) => (
              <Reveal as="li" key={p.t} delay={i * 0.04} className="group border-t border-navy/15 last:border-b">
                <div className="grid gap-3 py-8 transition-[padding] duration-500 ease-expo md:grid-cols-12 md:items-baseline md:hover:pl-4">
                  <span className="font-mono text-sm text-royal md:col-span-1">0{i + 1}</span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-navy transition-colors group-hover:text-royal sm:text-3xl md:col-span-6">{p.t}</h3>
                  <p className="text-base leading-relaxed text-slatey md:col-span-5">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>
      <ProcessSteps index="03" />
      <CtaBanner />
    </>
  );
}
